import styled, { keyframes } from 'styled-components'
import { media } from '../../../utils/mediaQuery'

// Shimmer animation for skeleton loading
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`

// Main card container with horizontal layout
export const CardContainer = styled.article<{ $isHovered?: boolean }>`
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  border: 1px solid ${({ $isHovered }) => ($isHovered ? '#0294df' : '#e6e6e6')};
  background-color: #ffffff;
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  min-height: 128px;

  &:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 6px 19px rgba(0, 0, 0, 0.12);
    border-color: #0294df;
  }

  ${media.tabletDown} {
    flex-direction: column;
    height: auto;
  }

  ${media.touch} {
    &:hover {
      transform: none;
    }
  }
`

// Left image section (40% on desktop)
export const ImageSection = styled.div`
  position: relative;
  width: 40%;
  min-width: 40%;
  overflow: hidden;

  ${media.tabletDown} {
    width: 100%;
    height: 160px;
  }

  ${media.mobileOnly} {
    height: 140px;
  }
`

// Gradient overlay on image
export const ImageGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
  pointer-events: none;
  z-index: 1;
`

// Actual image
export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

  ${CardContainer}:hover & {
    transform: scale(1.05);
  }
`

// Loading skeleton
export const ImageSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`

// Right content section (60% on desktop)
export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  padding-bottom: ${({ theme }) => theme.spacing.default};
  gap: 5px;
  flex: 1;
  min-width: 0;

  ${media.tabletDown} {
    padding: 12px;
    gap: 8px;
  }
`

// Content header with icon and address
export const ContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 3px;
`

// Icon wrapper
export const IconWrapper = styled.div`
  flex-shrink: 0;
`

// Header with address and title
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 0;
  flex: 1;
  min-width: 0;
`

// Address with location icon
export const Address = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: #666666;
  line-height: 1.3;
  min-width: 0;

  svg {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
  }
`

// Address text (truncated)
export const AddressText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Title
export const Title = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

// Badge container (property type + year)
export const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

// Property type badge (colored pill)
export const PropertyTypeBadge = styled.div<{
  $type?: 'house' | 'apartment' | null
}>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 9999px;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background-color: ${({ $type }) => {
    if ($type === 'house') return '#10B981'
    if ($type === 'apartment') return '#3B82F6'
    return '#6B7280'
  }};
  color: #ffffff;

  svg {
    width: 8px;
    height: 8px;
  }
`

// Year badge (neutral)
export const YearBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 9999px;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  background-color: #f3f4f6;
  color: #6b7280;
`

// Metrics row (3 columns)
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 3px;

  ${media.mobileOnly} {
    gap: 4px;
  }
`

// Individual metric item
export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 5px 6px;
  background: #f8f9fa;
  border-radius: 5px;

  ${media.mobileOnly} {
    padding: 4px 6px;
  }
`

// Metric value with icon
export const MetricValue = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;

  svg {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

// Metric label
export const MetricLabel = styled.span`
  font-size: 10px;
  color: #808080;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 400;
`

// Footer with timestamp and CTA
export const FooterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding-top: 5px;
  border-top: 1px solid #f5f5f5;
  margin-top: auto;
`

export const FooterMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  min-width: 0;
  flex: 1;
`

// Timestamp
export const Timestamp = styled.div`
  font-size: 10px;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: #808080;
  display: flex;
  align-items: center;
  gap: 3px;
  padding-block: ${({ theme }) => theme.spacing.micro};
  flex-shrink: 0;

  svg {
    width: 9px;
    height: 9px;
    opacity: 0.6;
  }
`

export const Creator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};

  svg {
    width: 9px;
    height: 9px;
    flex-shrink: 0;
    opacity: 0.6;
  }
`

export const CreatorName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// View button
export const ViewButton = styled.button`
  background: none;
  border: none;
  color: #0294df;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: all 200ms ease-out;
  border-radius: 5px;
  flex-shrink: 0;

  &:hover {
    background: rgba(2, 148, 223, 0.1);
  }

  svg {
    width: 10px;
    height: 10px;
  }

  ${media.touch} {
    min-height: 44px;
    padding: 10px 12px;
  }
`
